import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from "yup";
import ContactNumbers from 'src/components/contact_numbers/view';
import DynamicTable from "src/components/table";
import {
  columntype,
  dataLocalStorage,
  getLocalStorage,
  inputType,
  isValidArray,
} from "src/helper/publicFunction";
import {
  ManageContactUs,
  DeleteContactUs,
  EditContactUs,
  GetContact,
} from "./actions";
import { CommonAlert } from "src/components/alert/CommonAlert";

// Custom Contact Modal Component
const ContactModal = ({ 
  show, 
  onClose, 
  onSubmit, 
  editMode = false, 
  initialValues = {} 
}) => {
  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Name is required"),
    address: Yup.string().trim().required("Address is required"),
    email: Yup.string().email("Invalid email").trim().required("Email is required"),
    address_link: Yup.string().trim(),
    contact_numbers: Yup.array()
      .of(
        Yup.object().shape({
          type: Yup.string().required("Type is required"),
          number: Yup.string().required("Number is required"),
        })
      )
      .min(1, "At least one contact number is required"),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      address: '',
      email: '',
      address_link: '',
      contact_numbers: [{ type: 'phone', number: '' }],
      ...initialValues
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  return (
    <Dialog 
      open={show} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>
          <Typography variant="h5">
            {editMode ? 'Edit Contact' : 'Add New Contact'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {editMode ? 'Update contact information' : 'Fill in the contact details'}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                multiline
                rows={2}
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Link (Google Maps, etc.)"
                name="address_link"
                value={formik.values.address_link}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.address_link && Boolean(formik.errors.address_link)}
                helperText={formik.touched.address_link && formik.errors.address_link}
                placeholder="https://maps.google.com/..."
              />
            </Grid>

            {/* Contact Numbers Section */}
            <Grid item xs={12}>
              <Box mt={2}>
                <ContactNumbers
                  value={formik.values.contact_numbers}
                  onChange={(newNumbers) => {
                    formik.setFieldValue('contact_numbers', newNumbers);
                  }}
                  error={formik.touched.contact_numbers && Boolean(formik.errors.contact_numbers)}
                  helperText={formik.touched.contact_numbers && formik.errors.contact_numbers}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={onClose} 
            color="inherit"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained"
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {editMode ? 'Update Contact' : 'Add Contact'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const ContactUs = () => {
  const dispatch = useDispatch();
  const Commonalert = CommonAlert();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingEditdatatype, setloadingEditdatatype] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [values, setvalues] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [data, setData] = useState([]);
  const [TotalData, setTotalData] = useState(0);
  let dataPrivileges = getLocalStorage(dataLocalStorage.privileges);

  // Helper function to format contact numbers for display
  const formatContactNumbers = (contact_numbers) => {
    if (!isValidArray(contact_numbers)) return "No numbers";
    
    return contact_numbers
      .map(num => `${num.type}: ${num.number}`)
      .join(", ");
  };

  const GetData = () => {
    setLoadingData(true);
    setData([]);
    dispatch(
      GetContact({
        callback: (response) => {
          console.log("Server response:", response);
          const responseData = response?.data;
          console.log("Response data:", responseData);
          
          if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
            console.log("Converting object to array");
            const processedItem = {
              ...responseData,
              phone_display: formatContactNumbers(responseData.contact_numbers)
            };
            setData([processedItem]);
            setTotalData(1);
          } else if (isValidArray(responseData)) {
            console.log("Received array data");
            const processedData = responseData.map(item => ({
              ...item,
              phone_display: formatContactNumbers(item.contact_numbers)
            }));
            setData(processedData);
            setTotalData(processedData.length);
          } else {
            console.log("No valid data found");
            setData([]);
            setTotalData(0);
          }
          setLoadingData(false);
        },
      })
    );
  };

  useEffect(() => {
    GetData({ tableName: null, type: null });
  }, [page, rowsPerPage, search]);

  const columns = [
    { type: columntype.text, name: "id" },
    { type: columntype.text, name: "name" },
    { type: columntype.text, name: "address" },
    { type: columntype.text, name: "phone_display" },
    { type: columntype.text, name: "email" },
  ];

  const headCells = [
    {
      id: "id",
      numeric: false,
      disablePadding: false,
      label: "ID",
      withSort: true,
    },
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Name",
      withSort: false,
    },
    {
      id: "address",
      numeric: false,
      disablePadding: false,
      label: "Address",
      withSort: false,
    },
    {
      id: "phone_display",
      numeric: false,
      disablePadding: false,
      label: "Contact Numbers",
      withSort: false,
    },
    {
      id: "email",
      numeric: false,
      disablePadding: false,
      label: "Email",
      withSort: false,
    },
    (dataPrivileges["5"]?.access_edit == 1 ||
      dataPrivileges["5"]?.access_delete == 1) && {
      id: "action",
      numeric: false,
      disablePadding: false,
      label: "Actions",
      withSort: false,
    },
  ];

  const List = [
    dataPrivileges["5"]?.access_delete == 1 && {
      name: "Delete",
      function: async (row) => {
        if (row) {
          Commonalert.show({
            title: `Are you sure to delete ${row?.name ?? ""}?`,
            message: "You won't be able to revert this!",
            type: "delete",
            actions: [
              {
                name: "Yes",
                callback: () =>
                  dispatch(
                    DeleteContactUs({
                      callback: () => {
                        GetData({ tableName: null, type: null });
                      },
                      id: [row.id],
                    })
                  ),
                type: "delete",
              },
              {
                name: "No",
                callback: () => {},
                type: "cancel",
              },
            ],
          });
        }
      },
    },
    dataPrivileges["5"]?.access_edit == 1 && {
      name: "Edit",
      function: async (data) => {
        setEditMode(true);
        // Ensure contact_numbers is properly formatted for editing
        const editData = {
          ...data,
          contact_numbers: isValidArray(data.contact_numbers) 
            ? data.contact_numbers 
            : [{ type: 'phone', number: '' }]
        };
        setvalues(editData);
        setShowModal(true);
      },
    },
  ];

  const updateDataLocal = ({ id, failedName, newData }) => {
    setData((prevData) => {
      return prevData.map((item) => {
        if (item.id === id) {
          const updatedItem = {
            ...item,
            [failedName]: newData,
          };
          if (failedName === 'contact_numbers') {
            updatedItem.phone_display = formatContactNumbers(newData);
          }
          return updatedItem;
        }
        return item;
      });
    });
    setloadingEditdatatype("");
  };

  const handleModalSubmit = (values) => {
    dispatch(
      ManageContactUs({
        editMode: editMode,
        values: values,
        callback: (res) => {
          if (res) {
            setShowModal(false);
            GetData({ tableName: null, type: null });
          }
        },
      })
    );
  };

  return (
    <>
      <DynamicTable
        handleSearch={(event) => setSearch(event.target.value)}
        onRequestSort={(tableName, type) => {
          if (tableName && type) {
            GetData({ tableName, type });
          }
        }}
        onPressDelete={(ListIds) => {
          Commonalert.show({
            title: "Are you sure?",
            message: "You won't be able to revert this!",
            type: "delete",
            actions: [
              {
                name: "Yes",
                callback: () =>
                  dispatch(
                    DeleteContactUs({
                      callback: () => {
                        GetData({ tableName: null, type: null });
                      },
                      id: ListIds,
                    })
                  ),
                type: "delete",
              },
              {
                name: "No",
                callback: () => {},
                type: "cancel",
              },
            ],
          });
        }}
        onPressItem={(row) => {}}
        onPressAnyAction={(row, failedName, newData) => {
          // Handle inline editing if needed
        }}
        onPressAdd={() => {
          setvalues({
            name: '',
            address: '',
            email: '',
            address_link: '',
            contact_numbers: [{ type: 'phone', number: '' }]
          });
          setEditMode(false);
          setShowModal(true);
        }}
        items={data}
        columns={columns}
        headCells={headCells}
        page={page}
        setPage={setPage}
        selected={selected}
        setSelected={setSelected}
        search={search}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        Listdropdown={List}
        withOption={true}
        loadingData={loadingData}
        rowsPerPageOptions={[5, 10, 25, 50]}
        totalItem={TotalData}
        loadingEditdatatype={loadingEditdatatype}
        withAddButton={dataPrivileges["5"]?.access_write == 1 ? true : false}
        withDeleteItem={dataPrivileges["5"]?.access_delete == 1 ? true : false}
        withEditItem={dataPrivileges["5"]?.access_edit == 1 ? true : false}
        withPagination={false}
        withSearch={false}
      />

      {/* Custom Contact Modal */}
      <ContactModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleModalSubmit}
        editMode={editMode}
        initialValues={values}
      />
    </>
  );
};

export default ContactUs;